import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export type FormDirty = {
  // True while the form's current values differ from the baseline snapshot.
  isDirty: boolean;
  // How many fields differ, for "N unsaved changes".
  changeCount: number;
  // Increments after every change so UI derived from the DOM (validation,
  // summaries) recomputes against the committed values.
  formVersion: number;
  // Called by controls that mirror React state into hidden inputs: those fire
  // no native change event, so the form's onChange never sees them.
  markChanged: () => void;
  // Take the current values as the new baseline, e.g. after a successful save.
  resetBaseline: () => void;
};

export function changedEntryKeys(
  current: Map<string, string>,
  baseline: Map<string, string>,
): string[] {
  const keys = new Set([...current.keys(), ...baseline.keys()]);
  return [...keys].filter((key) => current.get(key) !== baseline.get(key));
}

export function useFormDirty(
  formRef: RefObject<HTMLFormElement | null>,
  resetKey?: unknown,
): FormDirty {
  const baselineRef = useRef<Map<string, string>>(new Map());
  const [dirtyKeys, setDirtyKeys] = useState<string[]>([]);
  const [formVersion, setFormVersion] = useState(0);
  const [changeTick, setChangeTick] = useState(0);

  const serializeForm = useCallback(() => {
    const entries = new Map<string, string>();
    if (!formRef.current) return entries;
    for (const [key, value] of new FormData(formRef.current).entries()) {
      entries.set(key, value as string);
    }
    return entries;
  }, [formRef]);

  const resetBaseline = useCallback(() => {
    baselineRef.current = serializeForm();
    setDirtyKeys([]);
  }, [serializeForm]);

  const markChanged = useCallback(() => {
    setChangeTick((tick) => tick + 1);
  }, []);

  // Deferred to after the commit: a control that mirrors state into a hidden
  // input calls markChanged() from its own handler, when the DOM still holds
  // the previous value. Reading it there would compare the form as it was
  // before the interaction and leave Save disabled until the next one.
  useEffect(() => {
    if (changeTick === 0) return;
    setDirtyKeys(changedEntryKeys(serializeForm(), baselineRef.current));
    setFormVersion((version) => version + 1);
  }, [changeTick, serializeForm]);

  // Re-runs after a reload remounts the form, so the baseline describes the
  // inputs actually on screen.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      baselineRef.current = serializeForm();
      setDirtyKeys([]);
      setFormVersion((version) => version + 1);
    });
    return () => cancelAnimationFrame(frame);
  }, [serializeForm, resetKey]);

  return {
    isDirty: dirtyKeys.length > 0,
    changeCount: dirtyKeys.length,
    formVersion,
    markChanged,
    resetBaseline,
  };
}
